import type { GlobalDesignSchemeState } from '@visualizer/types';
import {
    BorderMode,
    BorderSource,
    FacadeDirectionMode,
    FacadeType,
    ShadingAutomaticRectangleReference,
    VerticalFacadeCoverageMode,
} from '@visualizer/types';

export type DesignSchemesState = GlobalDesignSchemeState;

export const createDesignSchemesState = (): DesignSchemesState => ({
    sets: {},
    selectedSetId: undefined,
    defaults: {
        convexHull: {
            active: true,
            transparent: true,
            concave: false,
            concaveAlpha: { value: 10.0, minValue: 0.8, maxValue: 10.0 },
        },
        orientedVolume: {
            active: true,
            transparent: true,
            concave: false,
            concaveAlpha: { value: 10.0, minValue: 0.8, maxValue: 10.0 },
            step: { value: 3, minValue: 0.5, maxValue: 6 },
        },
        box: {
            active: true,
            transparent: true,
            snapToVolume: false,
            alpha: { value: 0, minValue: -180, maxValue: 180 },
        },

        floorBased: {
            active: true,
            linkToMainLayer: true,

            borderState: {
                mode: BorderMode.SINGLE,
                single: BorderSource.FOOTPRINT,
                external: BorderSource.FOOTPRINT,
                internal: BorderSource.FOOTPRINT,
                projectSketchIndex: 0,
                handDrawnBorder: [],
                handDrawnExternalBorder: [],
                handDrawnInternalBorder: [],
            },
            boxAlpha: { value: 0, minValue: -180, maxValue: 180 },

            groundFloorHeight: { value: 3, minValue: 2.7, maxValue: 6 },
            floorHeight: { value: 3, minValue: 2.7, maxValue: 6 }, // Grid.gridZ * Grid.gridRes }, // e.g. 6
            thickness: { value: 0.3, minValue: 0.1, maxValue: 0.6 },
            buildingHeight: { value: 6, minValue: 3, maxValue: 6 },

            voxelFloors: false,

            concave: true,
            concaveAlpha: { value: 10.0, minValue: 0.8, maxValue: 10.0 },

            facadeState: {
                hide: false,
                type: FacadeType.TRANSPARENT,
                componentTypes: [],
                componentLayerIds: {},
                componentLink: true,
                componentDirectionMode: FacadeDirectionMode.FLOOR_AVERAGE,
                directionMode: FacadeDirectionMode.VOLUME_AVERAGE,
                horizontalLength: { value: 2, minValue: 0.5, maxValue: 3 },
                horizontalFuseCoplanar: true,
                verticalLength: { value: 1.5, minValue: 0.5, maxValue: 10 },
                verticalCoverageMode: VerticalFacadeCoverageMode.FACING,
                verticalConnectCorners: true,
                verticalClosed: false,
                verticalHeight: { value: 3, minValue: 0, maxValue: 3 },
                verticalCustomAngle: false,
                verticalAngle: { value: 45, minValue: 0, maxValue: 90 },
                pvOffset: { value: 0, minValue: 0, maxValue: 3 },
                verticalHeightB: 0,
                balconyLength: { value: 2, minValue: 0.5, maxValue: 3 },
                balconyConnectCorners: true,
                balconyOnlyOnOpenings: false,
                greenDepth: { value: 0.5, minValue: 0.1, maxValue: 4 },
                shadingDepth: { value: 1, minValue: 0.5, maxValue: 2 },
                shadingManual: true,
                shadingConnectCorners: true,
                shadingAutomaticRectangle: false,
                shadingAutomaticRectangleReference: ShadingAutomaticRectangleReference.LONGER,
                shadingAutomaticLateralVerticalShade: false,
                stacking: false,
            },
        },
    },
});
